// CNC 파트너(제조사) 홈 — 로그인 직후 첫 화면 (2026-10-23, UI 구조 단계)
// 원칙: 아침에 열어 "오늘 뭘 깎고, 뭘 받고, 뭐가 문제인지" 10초 안에 보고 바로 작업으로. 설명·마케팅 요소 없음.
// 구성: 0 첫 방문(승인 대기·설정 체크리스트) / 1 상단 바 / 2 오늘 요약 카드 / 3 케이스 보드 / 5 하단 위젯
// 데이터는 샘플(partnerSample.js) — 서버 연결은 다음 단계.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Settings, Inbox, Clock, AlertTriangle, Wrench, Truck, CheckCircle2, Circle, X, Gauge, Building2, Hourglass } from 'lucide-react';
import { SAMPLE_PARTNER, SAMPLE_LABS, SAMPLE_NOTIFICATIONS, SAMPLE_USAGE, makeSampleCases } from './partnerSample';
import { useIsMobile, fmtMdHm, SampleNotice, ui } from './partnerUi';
import PartnerCaseBoard, { QUICK_FILTERS } from './PartnerCaseBoard';

const SUMMARY = [
    { key: 'newToday', icon: Inbox, color: '#2563eb', hint: '오늘 들어온 케이스' },
    { key: 'waiting', icon: Wrench, color: '#6366f1', hint: '아직 다운로드 안 함' },
    { key: 'dueSoon', icon: Clock, color: '#dc2626', hint: '납기 D-1 이하' },
    { key: 'jigFail', icon: AlertTriangle, color: '#ea580c', hint: '수동 확인 필요' },
    { key: 'unshipped', icon: Truck, color: '#b45309', hint: '밀링 중 3일 경과' },
];

const NOTI_COLOR = { new: '#2563eb', due: '#dc2626', jigFail: '#ea580c', unshipped: '#b45309', quota: '#6366f1' };

function PartnerHome() {
    const navigate = useNavigate();
    const mobile = useIsMobile();
    const [cases, setCases] = useState(makeSampleCases);
    const [quick, setQuick] = useState('all');
    const [notiOpen, setNotiOpen] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(true);
    const [checks, setChecks] = useState({ manager: false, engrave: false, invite: false });

    const counts = useMemo(() => {
        const out = {};
        SUMMARY.forEach((s) => { out[s.key] = cases.filter(QUICK_FILTERS[s.key].test).length; });
        return out;
    }, [cases]);

    const usagePct = Math.min(100, Math.round((SAMPLE_USAGE.used / SAMPLE_USAGE.cap) * 100));
    const labCount = SAMPLE_LABS.length;

    const CHECKLIST = [
        { key: 'manager', label: '담당자 추가', go: '/partner/settings' },
        { key: 'engrave', label: '각인 옵션 (로고 유무)', go: '/partner/settings' },
        { key: 'invite', label: '기공소 초대 링크 복사', go: '/partner/labs' },
    ];

    return (
        <div>
            {/* ── 1. 상단 바 ── */}
            <div style={{ ...styles.topBar, ...(mobile ? styles.topBarMobile : {}) }}>
                <div style={{ minWidth: 0 }}>
                    <div style={styles.company}>{SAMPLE_PARTNER.companyName}</div>
                    <div style={styles.manager}>{`담당 ${SAMPLE_PARTNER.managerName}`}</div>
                </div>
                <div style={styles.topActions}>
                    <div style={{ position: 'relative' }}>
                        <button type="button" style={styles.iconBtn} onClick={() => setNotiOpen((v) => !v)} aria-label="알림">
                            <Bell size={18} />
                            <span style={styles.dot}>{SAMPLE_NOTIFICATIONS.length}</span>
                        </button>
                        {notiOpen && (
                            <div style={{ ...styles.notiBox, ...(mobile ? styles.notiBoxMobile : {}) }}>
                                {SAMPLE_NOTIFICATIONS.map((n) => (
                                    <div key={n.id} style={styles.notiRow}>
                                        <span style={{ ...styles.notiDot, backgroundColor: NOTI_COLOR[n.kind] }} />
                                        <div style={{ minWidth: 0 }}>
                                            <div style={styles.notiText}>{n.text}</div>
                                            <div style={ui.muted}>{fmtMdHm(n.at)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button type="button" style={styles.iconBtn} onClick={() => navigate('/partner/settings')} aria-label="설정">
                        <Settings size={18} />
                    </button>
                </div>
            </div>

            <div style={mobile ? ui.bodyMobile : ui.body}>
                <SampleNotice />

                {/* ── 0. 가입 직후(첫 방문) ── */}
                {!SAMPLE_PARTNER.approved && (
                    <div style={styles.pendingBanner}>
                        <Hourglass size={16} />{'덴클 승인 후 케이스가 표시됩니다 (보통 1영업일)'}
                    </div>
                )}
                {showOnboarding && (
                    <div style={styles.onboard}>
                        <div style={styles.onboardHead}>
                            <span style={ui.cardTitle}>{'시작 설정'}</span>
                            <span style={ui.muted}>{`${Object.values(checks).filter(Boolean).length} / 3`}</span>
                            <span style={{ flex: 1 }} />
                            <button type="button" style={styles.closeBtn} onClick={() => setShowOnboarding(false)} aria-label="닫기"><X size={16} /></button>
                        </div>
                        <div style={styles.checkRow}>
                            {CHECKLIST.map((c, i) => (
                                <button
                                    key={c.key}
                                    type="button"
                                    style={{ ...styles.checkItem, ...(checks[c.key] ? styles.checkDone : {}) }}
                                    onClick={() => { setChecks((p) => ({ ...p, [c.key]: true })); navigate(c.go); }}
                                >
                                    {checks[c.key] ? <CheckCircle2 size={16} color="#047857" /> : <Circle size={16} color="#94a3b8" />}
                                    {`${i + 1}. ${c.label}`}
                                </button>
                            ))}
                        </div>
                        <p style={ui.cardSub}>{'아래 보드의 케이스는 화면 동작을 보여주는 샘플입니다.'}</p>
                    </div>
                )}

                {/* ── 2. 오늘 요약 ── */}
                <div style={{ ...styles.summaryRow, ...(mobile ? styles.summaryRowMobile : {}) }}>
                    {SUMMARY.map((s) => {
                        const Icon = s.icon;
                        const active = quick === s.key;
                        const n = counts[s.key];
                        return (
                            <button
                                key={s.key}
                                type="button"
                                onClick={() => setQuick(active ? 'all' : s.key)}
                                style={{ ...styles.sumCard, ...(active ? { borderColor: s.color, boxShadow: `0 0 0 1px ${s.color}` } : {}) }}
                            >
                                <div style={styles.sumHead}>
                                    <Icon size={15} color={s.color} />
                                    <span style={styles.sumLabel}>{QUICK_FILTERS[s.key].label}</span>
                                </div>
                                <div style={{ ...styles.sumNum, color: n > 0 ? s.color : '#cbd5e1' }}>{n}</div>
                                {!mobile && <div style={ui.muted}>{s.hint}</div>}
                            </button>
                        );
                    })}
                </div>

                {/* ── 3. 케이스 보드 (핵심) — 홈에서는 출고된 건은 숨기고 진행 중만 ── */}
                <PartnerCaseBoard
                    cases={cases}
                    setCases={setCases}
                    quick={quick}
                    onQuickChange={setQuick}
                    labs={SAMPLE_LABS}
                    hideShipped
                    mobile={mobile}
                />

                {/* ── 5. 하단 위젯 ── */}
                {!mobile && (
                    <div style={styles.widgetRow}>
                        <div style={{ ...ui.card, ...styles.widget }}>
                            <div style={styles.widgetHead}>
                                <Gauge size={15} color="#6366f1" />
                                <span style={ui.cardTitle}>{'이번 달 지그 사용량'}</span>
                                <span style={styles.widgetMain}>{`${SAMPLE_USAGE.used.toLocaleString()} / ${SAMPLE_USAGE.cap.toLocaleString()}개`}</span>
                            </div>
                            <div style={styles.barTrack}>
                                <div style={{ ...styles.barFill, width: `${usagePct}%`, backgroundColor: usagePct >= 90 ? '#dc2626' : '#6366f1' }} />
                            </div>
                            <div style={ui.muted}>{`${usagePct}% 사용 · 90% 넘으면 알림`}</div>
                        </div>
                        <div style={{ ...ui.card, ...styles.widget, cursor: 'pointer' }} onClick={() => navigate('/partner/labs')}>
                            <div style={styles.widgetHead}>
                                <Building2 size={15} color="#0f766e" />
                                <span style={ui.cardTitle}>{'기공소 유치 현황'}</span>
                            </div>
                            <div style={styles.labStat}>
                                <span>{'연결 기공소 '}<b style={styles.labNum}>{labCount}</b>{'곳'}</span>
                                <span style={ui.muted}>{'·'}</span>
                                <span>{'이번 달 신규 '}<b style={{ ...styles.labNum, color: '#0f766e' }}>{SAMPLE_USAGE.newLabsThisMonth}</b>{'곳'}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    topBar: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '14px 20px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
    },
    topBarMobile: { padding: '12px' },
    company: { fontSize: 17, fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    manager: { fontSize: 12, color: '#64748b', marginTop: 2 },
    topActions: { display: 'flex', gap: 6 },
    iconBtn: {
        position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 38, height: 38, border: '1px solid #e2e8f0', borderRadius: 10, backgroundColor: '#fff',
        color: '#334155', cursor: 'pointer',
    },
    dot: {
        position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999,
        backgroundColor: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    notiBox: {
        position: 'absolute', top: 44, right: 0, width: 320, backgroundColor: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, boxShadow: '0 10px 30px rgba(15,23,42,0.12)', zIndex: 50, padding: 6,
    },
    notiBoxMobile: { width: 'calc(100vw - 24px)', right: -50 },
    notiRow: { display: 'flex', gap: 10, padding: '10px 10px', borderRadius: 8, alignItems: 'flex-start' },
    notiDot: { width: 8, height: 8, borderRadius: '50%', marginTop: 6, flexShrink: 0 },
    notiText: { fontSize: 13, color: '#1e293b', lineHeight: 1.4 },
    pendingBanner: {
        display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 10,
        backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 14, fontWeight: 600,
    },
    onboard: { ...ui.card, display: 'flex', flexDirection: 'column', gap: 10, borderColor: '#c7d2fe', backgroundColor: '#fafaff' },
    onboardHead: { display: 'flex', alignItems: 'center', gap: 8 },
    closeBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 },
    checkRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    checkItem: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', border: '1px solid #e2e8f0',
        borderRadius: 9, backgroundColor: '#fff', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    checkDone: { color: '#047857', borderColor: '#a7f3d0', backgroundColor: '#f0fdf4' },
    summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 10 },
    summaryRowMobile: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 },
    sumCard: {
        textAlign: 'left', padding: 14, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
        cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4,
    },
    sumHead: { display: 'flex', alignItems: 'center', gap: 6 },
    sumLabel: { fontSize: 13, fontWeight: 700, color: '#334155' },
    sumNum: { fontSize: 28, fontWeight: 800, lineHeight: 1.1 },
    widgetRow: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 },
    widget: { display: 'flex', flexDirection: 'column', gap: 8, padding: 14 },
    widgetHead: { display: 'flex', alignItems: 'center', gap: 6 },
    widgetMain: { marginLeft: 'auto', fontSize: 14, fontWeight: 800, color: '#6366f1' },
    barTrack: { height: 8, borderRadius: 999, backgroundColor: '#eef2ff', overflow: 'hidden' },
    barFill: { height: '100%', borderRadius: 999 },
    labStat: { display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 14, color: '#334155' },
    labNum: { fontSize: 22, fontWeight: 800, color: '#0f172a' },
};

export default PartnerHome;
